import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Boxes, FileText } from 'lucide-react'
import { useBuilderObjectTypes } from './hooks/useBuilderObjectTypes'
import { BuilderTopHeader } from './BuilderNavTabs'
import { BuilderTypeCreateModal } from './BuilderTypeCreateModal'

/**
 * Accueil de la section « Types de contenu » du builder.
 *
 * Un type de contenu = un schéma de champs + UN modèle (la page qui en rend
 * les contenus à leur adresse publique) + ses contenus. Il appartient à un
 * site — la carte le dit. Pendant de `BuilderHome` pour les types (Actualité,
 * Événement…) au lieu des pages ; chaque type s'ouvre sur ses contenus.
 */
export const BuilderTypesHome = ({ embedded = false }) => {
  // Sous le menu orbit (`embedded`, routes `withMenu`) : remplir le `main`
  // d'OrbitChrome plutôt qu'imposer 100vh, sinon la page dépasse d'une hauteur
  // de barre de menu. Standalone : plein écran comme avant.
  const shellClass = `${embedded ? 'flex-1' : 'min-h-screen'} bg-gray-50`

  const { types, loading, saveType } = useBuilderObjectTypes()
  const [showModal, setShowModal] = useState(false)
  const navigate = useNavigate()

  return (
    <div className={shellClass}>
      <BuilderTopHeader
        rightSlot={
          types.length > 0 && (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 transition-colors"
            >
              <Plus size={14} />
              Nouveau type
            </button>
          )
        }
      />

      <main className="max-w-5xl mx-auto px-6 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-teal-700" />
          </div>
        ) : types.length === 0 ? (
          <EmptyState onCreateClick={() => setShowModal(true)} />
        ) : (
          <TypesList
            types={types}
            onCreateClick={() => setShowModal(true)}
            onOpen={(machineName) => navigate(`/builder/types/${machineName}`)}
          />
        )}
      </main>

      {showModal && (
        <BuilderTypeCreateModal
          onClose={() => setShowModal(false)}
          onCreate={async ({ name, type, definition, siteId }) => {
            const result = await saveType({ name, type, definition, siteId, create: true })
            setShowModal(false)
            navigate(`/builder/types/${result.type}`)
          }}
        />
      )}
    </div>
  )
}

const EmptyState = ({ onCreateClick }) => (
  <div className="flex flex-col items-center justify-center py-24 text-center">
    <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mb-6">
      <Boxes size={28} className="text-teal-600" />
    </div>
    <h1 className="text-2xl font-semibold text-gray-800 mb-2">
      Pas encore de type de contenu
    </h1>
    <p className="text-sm text-gray-400 max-w-md mb-8">
      Crée ton premier type (Actualité, Événement, Fiche produit…) : un schéma de
      champs, un modèle qui affiche chaque contenu à son adresse publique, et la
      liste de ces contenus.
    </p>
    <button
      onClick={onCreateClick}
      className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 transition-colors shadow-sm"
    >
      <Plus size={16} />
      Créer un type
    </button>
  </div>
)

const TypesList = ({ types, onCreateClick, onOpen }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <h1 className="text-lg font-semibold text-gray-800">Types de contenu</h1>
      <button
        onClick={onCreateClick}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 transition-colors"
      >
        <Plus size={14} />
        Nouveau type
      </button>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {types.map(type => (
        <TypeCard key={type.type} type={type} onOpen={onOpen} />
      ))}

      <button
        onClick={onCreateClick}
        className="flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed border-gray-200 text-gray-400 hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50 transition-all min-h-[120px]"
      >
        <Plus size={22} />
        <span className="text-sm font-medium">Nouveau type</span>
      </button>
    </div>
  </div>
)

const TypeCard = ({ type, onOpen }) => (
  <div
    className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-3 hover:border-teal-300 hover:shadow-sm transition-all cursor-pointer group"
    onClick={() => onOpen(type.type)}
  >
    <div className="w-full h-24 bg-gray-50 rounded-lg flex items-center justify-center border border-gray-100">
      <FileText size={20} className="text-gray-300" />
    </div>

    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-800 group-hover:text-teal-700 truncate transition-colors">
        {type.name || type.type}
      </p>
      <p className="text-xs text-gray-400 mt-0.5 truncate font-mono">{type.type}</p>
      <p className="text-xs text-gray-500 mt-1 truncate">
        {type.siteName || 'Commun à tous les sites'}
      </p>
    </div>
  </div>
)
